import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Logo from "./Logo";

const links = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services & Pricing" },
  { to: "/work", label: "Our Work" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

const Nav = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header className="sticky top-0 z-50 bg-[#F3EFE5]/95 backdrop-blur-md border-b border-[#E2DACB]" data-testid="site-header">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[60] focus:bg-[#233D35] focus:text-[#F3EFE5] focus:px-4 focus:py-2 focus:text-sm">
        Skip to content
      </a>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[72px] flex items-center justify-between">
        <Logo />
        <nav className="hidden lg:flex items-center gap-8" aria-label="Primary" data-testid="desktop-nav">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `link-rule font-head font-semibold uppercase tracking-[0.12em] text-sm transition-colors duration-200 ${
                  isActive ? "active text-[#233D35]" : "text-[#77786F] hover:text-[#233D35]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/contact"
            data-testid="nav-quote-cta"
            className="group inline-flex items-center gap-2 bg-[#C58A47] hover:bg-[#B0793A] text-[#F3EFE5] font-head font-bold uppercase tracking-[0.12em] text-sm px-5 py-3 transition-colors duration-200"
          >
            Get a Free Quote
            <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
        </nav>
        <button
          className="lg:hidden inline-flex items-center justify-center w-11 h-11 text-[#233D35]"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          aria-label={open ? "Close menu" : "Open menu"}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            key="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden border-t border-[#E2DACB] bg-[#F3EFE5]"
            aria-label="Mobile"
            data-testid="mobile-nav"
          >
            <div className="px-4 py-6 flex flex-col gap-1">
              {links.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.3 }}
                >
                  <NavLink
                    to={l.to}
                    end={l.to === "/"}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className={({ isActive }) =>
                      `block py-3 font-head font-bold uppercase tracking-wide text-2xl border-b border-[#E2DACB] ${
                        isActive ? "text-[#C58A47]" : "text-[#233D35]"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <Link
                to="/contact"
                onClick={() => setOpen(false)}
                data-testid="mobile-quote-cta"
                className="mt-5 inline-flex items-center justify-center gap-2 bg-[#C58A47] text-[#F3EFE5] font-head font-bold uppercase tracking-[0.12em] px-5 py-4"
              >
                Get a Free Quote <ArrowRight size={16} />
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Nav;
